// see https://github.com/jleonardo007/voice-recorder-ts-version
// and https://dev.to/jleonardo007/create-a-voice-recorder-with-react-32j6

import { useState, useEffect, useRef } from 'react'
import { Dispatch, SetStateAction } from 'react'

import { toast } from 'src/components/widgets/Toaster'
import { AssetType } from 'src/lib/assets/assetsTypes'
import bucketUpload, { readFile } from 'src/lib/assets/upload'
import { MAX_FILE_SIZE, MAX_RECORDER_TIME_IN_SECONDS } from 'src/lib/constants'

import useField, { UseFieldReturn } from '../../../lib/hooks/form/useField'

export type Recorder = {
  recordingSeconds: number
  isRecording: boolean
  isPaused: boolean
  recordFailed: boolean
  mediaStream: MediaStream | null
  mediaRecorder: MediaRecorder | null
  audio: string | null
}

export type UseRecorder = {
  recorderState: Recorder
  isSaving: boolean
  audio?: string
  field: UseFieldReturn<boolean>
  startRecording: () => void
  stopRecording: (cancelUpload?: boolean) => void
  pauseRecording: () => void
  resumeRecording: () => void
  clearRecording: () => void
  selectFileHandler: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>
}

export type Audio = {
  key: string
  audio: string
}

export type Interval = null | number | ReturnType<typeof setInterval>
export type SetRecorder = Dispatch<SetStateAction<Recorder>>
export type SetRecordings = Dispatch<SetStateAction<Audio[]>>
export type AudioTrack = MediaStreamTrack
export type MediaRecorderEvent = {
  data: Blob
}

const initialState: Recorder = {
  recordingSeconds: 0,
  isRecording: false,
  isPaused: false,
  recordFailed: false,
  mediaStream: null,
  mediaRecorder: null,
  /** @manual Audio holds the URL to the VoiceTip, either remote or local host. */
  audio: null,
}

export default function useRecorder({
  audio,
  name,
  type,
  id,
  onClear,
}: {
  audio: string | null
  name: string
  type: AssetType
  id: number
  onClear?: () => void
}) {
  const [recorderState, setRecorderState] = useState<Recorder>({
    ...initialState,
    audio,
  })
  const [uploadedAudio, setUploadedAudio] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const shouldUpload = useRef(true)
  const recordingInterval = useRef<number | null>(null)

  /** @manual Contains the URL to the VoiceTip or a local URL to a recording on the localhost. */
  const field = useField<boolean>(name)

  const uploadFromEvent = async (ev: BlobEvent) => {
    if (shouldUpload.current) {
      await upload(ev.data)
    }
  }

  const upload = async (blob: Blob) => {
    setIsSaving(true)
    try {
      if (blob) {
        await bucketUpload(type, id, blob)
      }
      field.onChange(!!blob)
    } catch (e) {
      field.onError({ type: 'validate', message: 'Saving failed' })
      console.error(e)
    }
    setIsSaving(false)
  }
  const cancelRecording = () => {
    window.URL.revokeObjectURL(recorderState.audio)
    setRecorderState(initialState)
  }

  const clearRecording = () => {
    if (uploadedAudio) {
      setUploadedAudio(null)
    }
    onClear?.()
    setRecorderState(initialState)

    field.onChange(false)
  }

  const selectFileHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    field.clearError()
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      if (file.size > MAX_FILE_SIZE) {
        toast(
          'error',
          'File limit exceeded. Please upload a file that is less than 100MB.'
        )
      } else if (file.type.startsWith('audio')) {
        upload(file)
        const audioDataUrl = (await readFile(file)) as string
        setUploadedAudio(audioDataUrl)
        field.onChange(true)
      } else {
        toast('error', 'Invalid file type, please upload an audio file.')
      }
    }
    e.target.value = ''
  }

  const stopListeningToInterval = () => {
    if (typeof recordingInterval.current === 'number')
      clearInterval(recordingInterval.current)
    recordingInterval.current = null
  }

  const stopRecording = (cancelUpload = false) => {
    if (recorderState.mediaRecorder.state !== 'inactive') {
      shouldUpload.current = !cancelUpload
      recorderState.mediaRecorder.stop()
    }
  }

  const pauseRecording = () => {
    if (recorderState.mediaRecorder.state !== 'inactive') {
      setRecorderState((prevState) => ({ ...prevState, isPaused: true }))
      recorderState.mediaRecorder.pause()
    }
  }

  const resumeRecording = () => {
    if (recorderState.mediaRecorder.state !== 'inactive') {
      setRecorderState((prevState) => ({ ...prevState, isPaused: false }))
      recorderState.mediaRecorder.resume()
    }
  }

  useEffect(() => {
    if (recorderState.isRecording && !recorderState.isPaused)
      recordingInterval.current = window.setInterval(() => {
        setRecorderState((prevState: Recorder) => {
          if (prevState.recordingSeconds === MAX_RECORDER_TIME_IN_SECONDS) {
            stopListeningToInterval()
            stopRecording()
            return prevState
          }
          return {
            ...prevState,
            recordingSeconds: prevState.recordingSeconds + 1,
          }
        })
      }, 1000)
    else stopListeningToInterval()

    return () => {
      stopListeningToInterval()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorderState.isRecording, recorderState.isPaused])

  useEffect(() => {
    if (recorderState?.mediaRecorder) {
      recorderState.mediaRecorder.addEventListener(
        'dataavailable',
        uploadFromEvent
      )
    }
    return () => {
      if (recorderState?.mediaRecorder) {
        recorderState.mediaRecorder.removeEventListener(
          'dataavailable',
          uploadFromEvent
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recorderState.mediaRecorder])

  useEffect(() => {
    setRecorderState((prevState) => {
      if (prevState.mediaStream)
        // MediaRecorder file type is inconsistent depending on the browser
        // so we need to convert the files directly on AWS
        return {
          ...prevState,
          mediaRecorder: new MediaRecorder(prevState.mediaStream),
        }
      else return prevState
    })
  }, [recorderState.mediaStream])

  useEffect(() => {
    const recorder = recorderState.mediaRecorder
    let chunks: Blob[] = []

    if (recorder && recorder.state === 'inactive') {
      recorder.start()

      recorder.ondataavailable = (e: MediaRecorderEvent) => {
        chunks.push(e.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/mpeg' })
        chunks = []
        setRecorderState((prevState: Recorder) => {
          if (prevState.mediaRecorder) {
            return {
              ...initialState,
              recordingSeconds: prevState.recordingSeconds,
              audio: window.URL.createObjectURL(blob),
            }
          } else return initialState
        })
      }
    }

    return () => {
      if (recorder)
        recorder.stream
          .getAudioTracks()
          .forEach((track: AudioTrack) => track.stop())
    }
  }, [recorderState.mediaRecorder])
  return {
    recorderState,
    isSaving,
    field,
    audio: recorderState.audio || uploadedAudio,
    startRecording: () => {
      field.clearError()
      cancelRecording()
      startRecording(setRecorderState)
    },
    stopRecording,
    pauseRecording,
    resumeRecording,
    selectFileHandler,
    clearRecording,
  }
}

async function startRecording(setRecorderState: SetRecorder) {
  try {
    const stream: MediaStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    })

    setRecorderState((prevState) => {
      return {
        ...prevState,
        recordFailed: false,
        isRecording: true,
        mediaStream: stream,
      }
    })
  } catch (err: unknown) {
    setRecorderState((prevState) => {
      return {
        ...prevState,
        isRecording: false,
        recordFailed: true,
      }
    })
  }
}
